import type { ReactNode } from "react";
import { Link } from "react-router";
import { Stack, buttonVariantClasses, cx } from "@wato/ui";

/**
 * UX-21: a gated page explains WHY it is blocked and offers the next step, instead of an empty
 * page or a silently disabled form. `preview` renders what the page does, inert and dimmed.
 */
export function BlockedState({
  title,
  reason,
  ctaLabel,
  ctaTo,
  preview,
}: {
  readonly title: string;
  readonly reason: string;
  readonly ctaLabel: string;
  readonly ctaTo: string;
  readonly preview?: ReactNode;
}) {
  return (
    <Stack gap={4}>
      <section
        role="status"
        aria-label={title}
        data-blocked-state
        className="rounded-lg border border-dashed border-line bg-surface p-6 text-center"
      >
        <Stack gap={2} className="items-center">
          <h3 className="text-lg font-semibold text-fg">{title}</h3>
          <p className="max-w-prose text-sm text-fg-muted">{reason}</p>
          <Link
            to={ctaTo}
            className={cx(
              "inline-flex h-10 items-center rounded-md px-4 text-sm font-medium",
              buttonVariantClasses.primary,
            )}
          >
            {ctaLabel}
          </Link>
        </Stack>
      </section>
      {preview ? (
        <div
          aria-hidden="true"
          inert
          data-blocked-preview
          className="pointer-events-none select-none opacity-50"
        >
          {preview}
        </div>
      ) : null}
    </Stack>
  );
}
